import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import '../pages/Dashboard.css';

/**
 * @param {object} [props.dashboardNav] - Present on /dashboard only: syncs highlight + table/view clicks with Dashboard state.
 * @param {string[]} [props.tables] - If provided (e.g. from Dashboard), skips fetching /data/tables.
 */
function AdminSidebar({ dashboardNav, tables: tablesProp }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [textsSectionOpen, setTextsSectionOpen] = useState(true);
  const [accountSectionOpen, setAccountSectionOpen] = useState(true);
  const [systemSectionOpen, setSystemSectionOpen] = useState(true);
  const [tablesLocal, setTablesLocal] = useState([]);
  const tables = tablesProp ?? tablesLocal;

  useEffect(() => {
    if (tablesProp != null) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get('/data/tables');
        if (!cancelled) setTablesLocal(res.data.tables || []);
      } catch {
        if (!cancelled) setTablesLocal([]);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [tablesProp]);

  const isDashboard = location.pathname === '/dashboard';

  const goDashboard = (state) => {
    navigate('/dashboard', { state });
  };

  const onTableClick = (table) => {
    if (isDashboard && dashboardNav) {
      dashboardNav.setActiveView('tables');
      dashboardNav.setSelectedTable(table);
      return;
    }
    goDashboard({ activeView: 'tables', selectedTable: table });
  };

  const onPassageSidebar = () => {
    navigate('/passage');
  };

  const onParagraphSidebar = () => {
    navigate('/paragraph');
  };

  const onSentenceSidebar = () => {
    navigate('/sentence');
  };

  const onShortSentenceSidebar = () => {
    navigate('/short-sentence');
  };

  const onWordSidebar = () => {
    navigate('/word');
  };

  const onTranslationQuestion = () => {
    navigate('/translation-question');
  };

  const onPassageQuestion = () => {
    navigate('/passage-question');
  };

  const onWritingSkill = () => {
    navigate('/writing-skill');
  };

  const onWritingSkillExample = () => {
    navigate('/writing-skill-example');
  };

  const onSchoolAccount = () => {
    navigate('/school-account');
  };

  const onCreateAccount = () => {
    navigate('/create-account');
  };

  const onSubscription = () => {
    navigate('/subscription');
  };

  const onDatabaseVersion = () => {
    navigate('/database-version');
  };

  const onLongQuestion = () => {
    if (isDashboard && dashboardNav) {
      dashboardNav.setActiveView('long-question');
      dashboardNav.setSelectedTable('');
      return;
    }
    goDashboard({ activeView: 'long-question', selectedTable: '' });
  };

  const onKeywords = () => {
    if (isDashboard && dashboardNav) {
      dashboardNav.setActiveView('keywords');
      dashboardNav.setSelectedTable('');
      return;
    }
    goDashboard({ activeView: 'keywords', selectedTable: '' });
  };

  const onMultipleChoice = () => {
    if (isDashboard && dashboardNav) {
      dashboardNav.setActiveView('multiple-choice');
      dashboardNav.setSelectedTable('');
      return;
    }
    goDashboard({ activeView: 'multiple-choice', selectedTable: '' });
  };

  const tableActive = (table) =>
    isDashboard &&
    dashboardNav &&
    dashboardNav.activeView === 'tables' &&
    dashboardNav.selectedTable === table;

  return (
    <aside className="sidebar">
      <div className="sidebar-section sidebar-section-flat">
        <ul className="sidebar-submenu">
          <li
            className={`sidebar-item ${location.pathname === '/' ? 'active' : ''}`}
            onClick={() => navigate('/')}
          >
            Overview
          </li>
        </ul>
      </div>
      <div className="sidebar-section">
        <div
          className="sidebar-section-header"
          onClick={() => setTextsSectionOpen((o) => !o)}
        >
          <span className="sidebar-section-title">Text</span>
          <span className="sidebar-arrow">{textsSectionOpen ? '▼' : '▶'}</span>
        </div>
        {textsSectionOpen && (
          <ul className="sidebar-submenu">
            {tables.filter((t) => String(t).trim()).map((table) => (
              <li
                key={table}
                className={`sidebar-item ${tableActive(table) ? 'active' : ''}`}
                onClick={() => onTableClick(table)}
              >
                {table}
              </li>
            ))}
            <li
              className={`sidebar-item ${location.pathname === '/passage' ? 'active' : ''}`}
              onClick={onPassageSidebar}
            >
              Passage
            </li>
            <li
              className={`sidebar-item ${location.pathname === '/paragraph' ? 'active' : ''}`}
              onClick={onParagraphSidebar}
            >
              Paragraph
            </li>
            <li
              className={`sidebar-item ${location.pathname === '/sentence' ? 'active' : ''}`}
              onClick={onSentenceSidebar}
            >
              Sentence
            </li>
            <li
              className={`sidebar-item ${location.pathname === '/short-sentence' ? 'active' : ''}`}
              onClick={onShortSentenceSidebar}
            >
              Short sentence
            </li>
            <li
              className={`sidebar-item ${location.pathname === '/word' ? 'active' : ''}`}
              onClick={onWordSidebar}
            >
              Word
            </li>
            <li
              className={`sidebar-item ${location.pathname === '/translation-question' ? 'active' : ''}`}
              onClick={onTranslationQuestion}
            >
              Translation Question
            </li>
            <li
              className={`sidebar-item ${location.pathname === '/passage-question' ? 'active' : ''}`}
              onClick={onPassageQuestion}
            >
              Passage Question
            </li>
            <li
              className={`sidebar-item ${location.pathname === '/writing-skill' ? 'active' : ''}`}
              onClick={onWritingSkill}
            >
              Writing Skill
            </li>
            <li
              className={`sidebar-item ${location.pathname === '/writing-skill-example' ? 'active' : ''}`}
              onClick={onWritingSkillExample}
            >
              Writing Skill Example
            </li>
            <li
              className={`sidebar-item ${
                isDashboard && dashboardNav?.activeView === 'long-question' ? 'active' : ''
              }`}
              onClick={onLongQuestion}
            >
              Long Question
            </li>
            <li
              className={`sidebar-item ${
                isDashboard && dashboardNav?.activeView === 'keywords' ? 'active' : ''
              }`}
              onClick={onKeywords}
            >
              Keyword
            </li>
            <li
              className={`sidebar-item ${
                isDashboard && dashboardNav?.activeView === 'multiple-choice' ? 'active' : ''
              }`}
              onClick={onMultipleChoice}
            >
              Multiple Choice
            </li>
          </ul>
        )}
      </div>

      <div className="sidebar-section">
        <div
          className="sidebar-section-header"
          onClick={() => setAccountSectionOpen((o) => !o)}
        >
          <span className="sidebar-section-title">Account</span>
          <span className="sidebar-arrow">{accountSectionOpen ? '▼' : '▶'}</span>
        </div>
        {accountSectionOpen && (
          <ul className="sidebar-submenu">
            <li
              className={`sidebar-item ${location.pathname === '/school-account' ? 'active' : ''}`}
              onClick={onSchoolAccount}
            >
              School Account
            </li>
            <li
              className={`sidebar-item ${location.pathname === '/create-account' ? 'active' : ''}`}
              onClick={onCreateAccount}
            >
              Create Account
            </li>
            <li
              className={`sidebar-item ${location.pathname === '/subscription' ? 'active' : ''}`}
              onClick={onSubscription}
            >
              Subscription
            </li>
          </ul>
        )}
      </div>

      <div className="sidebar-section">
        <div
          className="sidebar-section-header"
          onClick={() => setSystemSectionOpen((o) => !o)}
        >
          <span className="sidebar-section-title">System</span>
          <span className="sidebar-arrow">{systemSectionOpen ? '▼' : '▶'}</span>
        </div>
        {systemSectionOpen && (
          <ul className="sidebar-submenu">
            <li
              className={`sidebar-item ${location.pathname === '/database-version' ? 'active' : ''}`}
              onClick={onDatabaseVersion}
            >
              Database Version
            </li>
          </ul>
        )}
      </div>
    </aside>
  );
}

export default AdminSidebar;
